/*
 * © 2026 eggs unimedia GmbH
 * MSAL Auth Service — Microsoft Multi-Tenant Login via Custom Auth Challenge
 */

import { signIn, signUp } from 'aws-amplify/auth'

const AZURE_CLIENT_ID = process.env.REACT_APP_AZURE_CLIENT_ID || ''

const msalConfig = {
  auth: {
    clientId: AZURE_CLIENT_ID,
    authority: 'https://login.microsoftonline.com/common',
    redirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'sessionStorage',
  },
}

let msalInstance = null
let initialized = false

async function getMsalInstance() {
  if (!msalInstance) {
    const { PublicClientApplication } = await import('@azure/msal-browser')
    msalInstance = new PublicClientApplication(msalConfig)
  }
  if (!initialized) {
    await msalInstance.initialize()
    initialized = true
  }
  return msalInstance
}

// Generate a secure random password for the Cognito user (never used by user)
function generateRandomPassword() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%'
  const array = new Uint8Array(24)
  crypto.getRandomValues(array)
  return Array.from(array, (b) => chars[b % chars.length]).join('')
}

export const msalAuthService = {
  async login() {
    const instance = await getMsalInstance()

    // Step 1: Authenticate with Microsoft via popup
    const loginResponse = await instance.loginPopup({
      scopes: ['openid', 'email', 'profile'],
    })

    const idToken = loginResponse.idToken
    const email = (loginResponse.idTokenClaims.email || loginResponse.idTokenClaims.preferred_username).toLowerCase()
    const name = loginResponse.idTokenClaims.name || email.split('@')[0]

    // Step 2: Try Cognito Custom Auth signIn first (existing user)
    try {
      const { nextStep } = await signIn({
        username: email,
        options: { authFlowType: 'CUSTOM_WITHOUT_SRP' },
      })

      if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
        const { confirmSignIn } = await import('aws-amplify/auth')
        await confirmSignIn({ challengeResponse: idToken })
      }

      return { email, name }
    } catch (signInError) {
      // User doesn't exist yet — sign up
      // Note: PreventUserExistenceErrors makes Cognito return generic "Incorrect username or password"
      if (signInError.name === 'UserNotFoundException' ||
          signInError.name === 'NotAuthorizedException' ||
          signInError.message?.includes('User does not exist') ||
          signInError.message?.includes('Incorrect username or password')) {
        const password = generateRandomPassword()

        try {
          await signUp({
            username: email,
            password,
            options: {
              userAttributes: { email, name },
              // Send token via both paths — Amplify v6 has a known bug where
              // clientMetadata is not reliably passed to Pre-Sign-Up Lambda
              clientMetadata: { msalIdToken: idToken },
              validationData: { msalIdToken: idToken },
            },
          })
        } catch (signUpError) {
          // Race condition: user was created between signIn and signUp
          if (signUpError.name !== 'UsernameExistsException') throw signUpError
        }

        // After signup (auto-confirmed by PreSignUp Lambda), sign in with custom challenge
        const { nextStep } = await signIn({
          username: email,
          options: { authFlowType: 'CUSTOM_WITHOUT_SRP' },
        })

        if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
          const { confirmSignIn } = await import('aws-amplify/auth')
          await confirmSignIn({ challengeResponse: idToken })
        }

        return { email, name }
      }

      throw signInError
    }
  },
}
